/**
 * MilestoneTimeline: Horizontal timeline bar showing strategic milestones.
 *
 * Tasks with tier >= 7 render as milestone markers positioned by due_date.
 * Tier 7 = milestone, Tier 8 = launch, Tier 9 = strategic goal.
 */

import { Target, Rocket, Trophy } from 'lucide-react';
import type { TaskItem } from '@/api/types';

const TIER_CONFIG: Record<number, { icon: typeof Target; color: string; label: string }> = {
  7: { icon: Target, color: '#06b6d4', label: 'Milestone' },
  8: { icon: Rocket, color: '#f97316', label: 'Launch' },
  9: { icon: Trophy, color: '#eab308', label: 'Goal' },
};

interface MilestoneTimelineProps {
  tasks: TaskItem[];
  onSelectTask: (task: TaskItem) => void;
}

export function MilestoneTimeline({ tasks, onSelectTask }: MilestoneTimelineProps) {
  // Filter to tier >= 7 milestones
  const milestones = tasks
    .filter((t) => t.tier >= 7)
    .sort((a, b) => {
      // Sort by due_date, undated last
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });

  if (milestones.length === 0) return null;

  // Calculate timeline range
  const now = new Date();
  const datedMilestones = milestones.filter((m) => m.due_date);
  const dates = datedMilestones.map((m) => new Date(m.due_date!).getTime());
  const minDate = dates.length > 0 ? Math.min(now.getTime(), ...dates) : now.getTime();
  const maxDate = dates.length > 0 ? Math.max(now.getTime() + 90 * 86400000, ...dates) : now.getTime() + 180 * 86400000;
  const range = maxDate - minDate || 1;

  const getPosition = (dateStr: string | null): number => {
    if (!dateStr) return 95; // undated milestones go to far right
    const pos = ((new Date(dateStr).getTime() - minDate) / range) * 90 + 5;
    return Math.max(5, Math.min(95, pos));
  };

  const nowPosition = ((now.getTime() - minDate) / range) * 90 + 5;

  return (
    <div className="border border-gray-800 rounded-lg p-4 bg-gray-900/20">
      <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-3">
        Milestones & Goals
      </div>

      {/* Timeline bar */}
      <div className="relative h-16">
        {/* Track */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-800" />

        {/* Now marker */}
        <div
          className="absolute top-0 bottom-0 w-px bg-[#39FF14]/30"
          style={{ left: `${Math.max(5, Math.min(95, nowPosition))}%` }}
        >
          <span className="absolute -top-0.5 -translate-x-1/2 text-[8px] font-mono text-[#39FF14]">NOW</span>
        </div>

        {/* Milestone markers */}
        {milestones.map((milestone) => {
          const config = TIER_CONFIG[milestone.tier] || TIER_CONFIG[7];
          const Icon = config.icon;
          const pos = getPosition(milestone.due_date);
          const isDone = milestone.status === 'done';
          const isOverdue = milestone.due_date && milestone.status !== 'done'
            && milestone.status !== 'cancelled'
            && new Date(milestone.due_date) < now;

          let markerColor = config.color;
          if (isDone) markerColor = '#22c55e';
          if (isOverdue) markerColor = '#ef4444';

          return (
            <button
              key={milestone.id}
              onClick={() => onSelectTask(milestone)}
              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 group"
              style={{ left: `${pos}%` }}
              title={`${milestone.title}${milestone.due_date ? ` — ${new Date(milestone.due_date).toLocaleDateString()}` : ''}`}
            >
              {/* Marker */}
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-transform group-hover:scale-125 ${isDone ? 'bg-green-900/30' : 'bg-gray-900'}`}
                style={{ borderColor: markerColor }}
              >
                <Icon className="w-3.5 h-3.5" style={{ color: markerColor }} />
              </div>

              {/* Label */}
              <div className="absolute top-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                <div className="text-[9px] font-mono font-bold truncate max-w-24" style={{ color: markerColor }}>
                  {milestone.title}
                </div>
                {milestone.due_date && (
                  <div className="text-[8px] font-mono text-gray-600 text-center">
                    {new Date(milestone.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
